import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setupKeepAlive } from '../hooks/useCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem('auth_token');
    } catch (e) {
      console.error('Error reading token:', e);
      return null;
    }
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Keep-alive désactivé pour éviter les appels répétés
  // useEffect(() => {
  //   const cleanupKeepAlive = setupKeepAlive(45000); // Ping toutes les 45 secondes
  //   return cleanupKeepAlive;
  // }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      // console.log('[AUTH] Starting auth check, token:', !!token);
      
      // Timeout pour éviter le blocage infini
      const timeoutId = setTimeout(() => {
        // console.warn('[AUTH] Auth check timeout - forcing loading false');
        setLoading(false);
      }, 1500); // Timeout réduit à 1.5 secondes

      try {
        // Si pas de token, pas besoin de vérifier
        if (!token) {
          // console.log('[AUTH] No token found, skipping auth check');
          clearTimeout(timeoutId);
          setLoading(false);
          return;
        }
        
        if (token) {
          // Vérifier d'abord le cache utilisateur
          let cachedUser, cacheTimestamp;
          try {
            cachedUser = localStorage.getItem('cached_user');
            cacheTimestamp = localStorage.getItem('user_cache_timestamp');
          } catch (e) {
            console.error('Error reading localStorage:', e);
            // Si erreur localStorage, nettoyer et continuer
            localStorage.removeItem('cached_user');
            localStorage.removeItem('user_cache_timestamp');
            localStorage.removeItem('auth_token');
            setToken(null);
            clearTimeout(timeoutId);
            setLoading(false);
            return;
          }
          
          const now = Date.now();
          
          // Si on a un cache valide (moins de 5 minutes), l'utiliser pour accélérer le chargement
          if (cachedUser && cacheTimestamp && (now - parseInt(cacheTimestamp)) < 5 * 60 * 1000) {
            try {
              const parsedUser = JSON.parse(cachedUser);
              setUser(parsedUser);
              clearTimeout(timeoutId);
              setLoading(false);
              return;
            } catch (e) {
              console.error('Error parsing cached user:', e);
              // Cache corrompu, le supprimer
              localStorage.removeItem('cached_user');
              localStorage.removeItem('user_cache_timestamp');
            }
          }
          
          // Requête réseau avec timeout
          const controller = new AbortController();
          const requestTimeout = setTimeout(() => controller.abort(), 1500);
          
          const response = await axios.get(`${API_BASE_URL}/api/me`, {
            signal: controller.signal
          });
          
          clearTimeout(requestTimeout);
          const userData = response.data;
          setUser(userData);
          
          // Mettre en cache les données utilisateur
          try {
            localStorage.setItem('cached_user', JSON.stringify(userData));
            localStorage.setItem('user_cache_timestamp', now.toString());
          } catch (e) {
            console.error('Error saving to localStorage:', e);
          }
          
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        
        // Si erreur réseau ou token invalide, nettoyer et déconnecter
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('cached_user');
          localStorage.removeItem('user_cache_timestamp');
        } catch (e) {
          console.error('Error clearing localStorage:', e);
        }
        
        setToken(null);
        setUser(null);
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    checkAuth();
  }, [token, API_BASE_URL]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        email,
        password
      });

      const { access_token } = response.data;
      
      // Get user info before setting any state
      const userResponse = await axios.get(`${API_BASE_URL}/api/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      // Set all auth state atomically to avoid race conditions
      setToken(access_token);
      setUser(userResponse.data);
      localStorage.setItem('auth_token', access_token);
      
      // Mettre en cache les données utilisateur après login
      const now = Date.now();
      localStorage.setItem('cached_user', JSON.stringify(userResponse.data));
      localStorage.setItem('user_cache_timestamp', now.toString());
      
      return { success: true, user: userResponse.data };
    } catch (error) {
      console.error('Login failed:', error);
      let errorMessage = 'Erreur de connexion';
      
      if (error.response) {
        // Erreur de réponse du serveur
        if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 401) {
          errorMessage = 'Email ou mot de passe incorrect';
        } else if (error.response.status >= 500) {
          errorMessage = 'Erreur serveur, veuillez réessayer';
        }
      } else if (error.request) {
        // Problème de réseau
        errorMessage = 'Impossible de contacter le serveur';
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/register`, userData);
      return { success: true, user: response.data };
    } catch (error) {
      console.error('Registration failed:', error);
      let errorMessage = 'Erreur lors de l\'inscription';
      
      if (error.response) {
        if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 422) {
          errorMessage = 'Données invalides, vérifiez les champs';
        } else if (error.response.status === 409) {
          errorMessage = 'Cet email est déjà utilisé';
        } else if (error.response.status >= 500) {
          errorMessage = 'Erreur serveur, veuillez réessayer';
        }
      } else if (error.request) {
        errorMessage = 'Impossible de contacter le serveur';
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('cached_user');
    localStorage.removeItem('user_cache_timestamp');
    delete axios.defaults.headers.common['Authorization'];
    // Redirect to home page after logout
    window.location.href = '/';
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};